import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarOfLife, Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Truck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Retour de la connexion Google gérée par Emergent : l'URL contient #session_id=...
 * On échange ce jeton côté serveur, puis on ouvre la session PACEMIA.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [signup, setSignup] = useState(null); // {ticket, email, name}
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState({}, "", window.location.pathname);
    if (!sessionId) { setError("Session Google introuvable."); return; }
    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        if (data.needs_signup) {
          setSignup(data);
          setCompany(data.name || "");
        } else {
          setUser(data);
          // replaceState ne notifie pas React Router : on resynchronise sa location
          // (sinon AppShell continue d'afficher AuthCallback indéfiniment).
          navigate("/", { replace: true });
        }
      })
      .catch((err) => setError(apiError(err)));
  }, [location.hash, setUser, navigate]);

  const createCompany = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/google/complete", {
        ticket: signup.ticket, role: "gerant", company_name: company,
      });
      toast.success("Bienvenue sur PACEMIA — votre essai gratuit démarre");
      setUser(data);
      navigate("/", { replace: true });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full p-6 text-center space-y-4" data-testid="google-error">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
          <p className="font-semibold">Connexion Google impossible</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={() => { window.location.href = "/login"; }} data-testid="google-error-back">
            Retour à la connexion
          </Button>
        </Card>
      </div>
    );
  }

  if (signup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-lg w-full p-6 space-y-5" data-testid="google-signup">
          <Logo size={30} />
          <div>
            <h1 className="font-display text-2xl font-bold">Choisissez votre type de compte</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Aucun compte PACEMIA n'est associé à <strong>{signup.email}</strong>.
            </p>
          </div>

          <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-3">
            <p className="font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Gérant de société de transport</p>
            <p className="text-xs text-muted-foreground">
              Régulation, planning, chauffeurs, facturation. Essai gratuit, sans carte bancaire.
            </p>
            <div>
              <Label className="text-xs">Nom de votre société</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} className="mt-1"
                placeholder="Ambulances Dupont" data-testid="google-company-name" />
            </div>
            <Button className="w-full" onClick={createCompany} disabled={busy || !company.trim()} data-testid="google-create-company">
              {busy ? "Création…" : "Créer mon espace société"}
            </Button>
          </div>

          <div className="rounded-lg border border-border p-4 space-y-2">
            <p className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4" /> Je suis chauffeur</p>
            <p className="text-xs text-muted-foreground">
              Votre compte est créé par votre employeur. Demandez-lui d'enregistrer cette adresse
              (<strong>{signup.email}</strong>) dans votre fiche conducteur, puis reconnectez-vous avec Google.
            </p>
          </div>

          <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline"
            onClick={() => { window.location.href = "/login"; }} data-testid="google-signup-cancel">
            Annuler et revenir à la connexion
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse"><StarOfLife size={56} /></div>
    </div>
  );
}
